import * as React from 'react';
import type { RouteComponentProps, RouterState } from 'react-router';
import { Route } from 'react-router';

import { Route as CourseraRoute } from '@coursera/react-router';

import loadOnRoute from 'bundles/common/components/loadOnRoute';
import NotFound from 'bundles/phoenix/components/NotFound';
import { CONSUMER_SEARCH_ROUTE } from 'bundles/search-common/constants';
import { inferPageNumberFromParams } from 'bundles/search-common/utils/eventingUtils';
import { isRolledOutSearchPageConfig } from 'bundles/search-common/utils/pageConfigUtils';
import SearchApp from 'bundles/search-v2/components/SearchV2Application';
import UnifiedUserExperienceHandler from 'bundles/unified-common/providers/UnifiedUserExperienceHandler';

// Original search page component
const OriginalSearchPageRoot = loadOnRoute(() => import('bundles/search-v2/components/SearchPageRoot'));

// New search page component using page config system
const PageConfigSearchPageRoot = loadOnRoute(() => import('bundles/search-common/pages/SearchPageRoot'));

// Conditionally render the appropriate search page based on query and feature flags
const ConditionalSearchPageRoot = (
  nextState: RouterState,
  callback: (err: Error | null, component?: React.ComponentType<RouteComponentProps<{}, {}>>) => void
) => {
  if (isRolledOutSearchPageConfig()) {
    // Load the page config version for free queries when experiment is enabled
    PageConfigSearchPageRoot(nextState, callback);
  } else {
    // Load the original search page for all other cases
    OriginalSearchPageRoot(nextState, callback);
  }
};

const SearchAppWithUserExperienceHandler = (props: React.ComponentProps<typeof SearchApp>) => (
  <UnifiedUserExperienceHandler>
    <SearchApp {...props} />
  </UnifiedUserExperienceHandler>
);

export default (
  <CourseraRoute
    path="/"
    component={SearchAppWithUserExperienceHandler}
    handle={{
      tracking: {
        data: (_, location) => {
          const currentPage = inferPageNumberFromParams(location?.query?.page);

          return {
            page: {
              segment: 'consumer',
              type: 'search_result_page',
              pagination: {
                pageNumber: currentPage ? Number(currentPage) : 1,
              },
            },
          };
        },
      },
    }}
  >
    <Route path={CONSUMER_SEARCH_ROUTE} getComponent={ConditionalSearchPageRoot} />
    <Route path="search-v2" getComponent={ConditionalSearchPageRoot} />
    <Route path="*" component={NotFound} />
  </CourseraRoute>
);
